/**
 * Deal Dossier — Capital Stack tab.
 *
 * Redesigned (Oct 2026) around what a lender reads on a capitalization page,
 * after the owner's review: "it feels like it's the same piece of information
 * shown 10 different ways." It drew the one recorded tranche as a structure
 * table, a column chart, a donut, a waterfall and a radial gauge, beside a
 * Sources & Uses that called the sponsor's equity an error ("Uses exceed
 * sources by $44,000,000") and an equity summary that had no equity.
 *
 * Now each fact is drawn once:
 *   - the leverage KPIs ride the dossier's one metric bar (LTV / LTC, debt
 *     yield, DSCR, loan per unit, equity, total capitalization);
 *   - Capitalization: ONE stack bar with every tranche's dollars, share,
 *     attachment points and per-unit basis, closed by the equity — stated, or
 *     implied as uses (or value) less debt and labelled so;
 *   - Coverage & Sizing: each ratio with the operands it is made of, which is
 *     what a credit reader checks; no threshold or sizing constraint is shown
 *     that the record does not state;
 *   - Sources & Uses, balanced by the implied equity plug rather than flagged,
 *     with a plain note when the sponsor's stated equity differs;
 *   - Loan Terms & Pricing, the stated terms with the unstated ones named in
 *     one line instead of a column of dashes;
 *   - Covenants & Record, folded into one compact panel.
 *
 * Unlocked, every value the tab prints is edited where it is printed (owner,
 * 21 Oct), and unlocking moves no panel: a stated field becomes its input in
 * place of the text, a figure this page computes opens the inputs it is made
 * of (R1), and a line naming what a blank figure waits on opens those inputs.
 * There used to be an "Inputs" block appended under three panels and a
 * re-laid Loan Terms grid, so at 1366x768 every panel body scrolled in the
 * mode the owner edits in. One input per field on a screen: a field the
 * metric bar already edits (the loan, the value, LTV, DSCR) or another panel
 * owns is printed read-only here and says where it is edited.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Calculator, Check, Scale, X } from 'lucide-react'

import type { DealFieldDef, RawDealFile } from '@/services/dealFile'
import type { DealDocument } from '@/services/documents'
import { receivedByEmail } from './documentsModel'
import { HEADER_FIELDS, useProvideDossierMetrics, type DossierMetric } from './MetricBar'
import { structuresService } from '@/services/structures'
import { loansService } from '@/services/loans'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import type { LoanActivityEvent } from '@/pages/pipeline-intelligence/types'
import type { DealDossierTab } from '@/store/dealDossierStore'
import { DossierPanel, renderCatalogueInput } from './ui'
import { useDossierEdit, type DossierEdit } from './editContext'
import { EditableValue, valueEditMode } from './EditableValue'
import { activateOnKey, EditPopover, InputEditorList, outputFormulaOf } from './computedInputs'
import { isSystemField, isWritableField, type EditTarget } from './editableFields'
import { formatAmount } from './format'
import { equityGapBasis, rateLabel, sponsorEquityFormula, type CapitalStackModel } from './capitalStackModel'
import { FINANCING_EDITORS, useEconomicsModel } from './EconomicsPage'
import { financingDisplay } from './economicsModel'
import { isDerivedDateField } from './derivedDates'
import { OwnershipMark } from './valueStateChip'
import { useCapitalization } from './useCapitalization'
import { attachmentRange, CAPITAL_INPUTS, DEBT_STEPS, EQUITY_STEPS, type LeverageFigure, type LeverageId, type LeverageModel } from './leverageModel'
import {
  AmountMarks,
  CapitalizationBar,
  FigureMarks,
  RequestTag,
  Swatch,
} from './CapitalizationView'
import { AttachmentTrack, GaugeBar, HeadroomBar, ShareBar } from './CapitalStackGauge'
import { figureGauges, gaugeStateOf, shareOf, type GaugeId } from './capitalStackGauges'
import './capitalStack.css'

function moneyExact(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `$${Math.round(value).toLocaleString()}`
}

function when(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function PanelLink({ label, onClick }: { label: string; onClick?: () => void }) {
  if (!onClick) return null
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 font-sans text-[10px] normal-case tracking-normal text-amber-300/90 hover:text-amber-200"
    >
      {label} <ArrowRight size={10} />
    </button>
  )
}

/** An empty state that takes the height its panel has, as an outline of what
 *  will be drawn there: a dashed block with the one line it waits on, so a
 *  panel with nothing recorded reads as waiting, not as unfinished. */
function FillEmpty({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  return (
    <div
      data-fill-empty=""
      className={`flex flex-1 flex-col items-center justify-center rounded border border-dashed border-white/[0.08] px-4 text-center text-[11px] leading-snug text-stone-500 ${compact ? 'cs-fill-compact min-h-[26px] py-1' : 'min-h-[44px] py-3'}`}
    >
      {children}
    </div>
  )
}

/** How many documents and activity lines the Covenants & Record panel lists at
 *  its usual height, and how many it lists when the grid is tall enough to have
 *  the room (capitalStack.css shows the extra lines only then, so they never
 *  add to the height a row needs). */
const DOCUMENTS_SHOWN = 3
const DOCUMENTS_SHOWN_TALL = 6
const ACTIVITY_SHOWN = 2
const ACTIVITY_SHOWN_TALL = 6

/** The ratios the Coverage & Sizing table works through, in reading order. */
const SIZING_FIGURES: LeverageId[] = ['ltv', 'ltc', 'debt_yield', 'dscr', 'loan_per_unit', 'basis_per_unit', 'cap_rate']

/** The Financing Summary rows that are the loan's terms — the leverage and
 *  yield rows are the Coverage & Sizing table's, and the loan amount the
 *  capitalization's. */
const TERM_ROWS = [
  'Position', 'Loan Type', 'Rate Type', 'Interest Rate', 'Index Rate Type', 'Spread', 'Floor', 'Day Count',
  'Closing Date', 'Loan Term', 'Maturity', 'Amortization', 'IO Period', 'Extension Options', 'Extensions', 'Extension Maturity',
  'Payment Frequency', 'Origination Fee', 'Exit Fee', 'Prepayment',
]

/** The terms as a term sheet groups them, in reading order. Every TERM_ROWS
 *  label is in exactly one group. */
const TERM_GROUPS: { id: string; label: string; rows: string[] }[] = [
  { id: 'structure', label: 'Structure & rate', rows: ['Position', 'Loan Type', 'Rate Type', 'Interest Rate', 'Index Rate Type', 'Spread', 'Floor', 'Day Count'] },
  {
    id: 'term',
    label: 'Term & amortization',
    rows: [
      'Closing Date', 'Loan Term', 'Maturity', 'Extension Options', 'Extensions', 'Extension Maturity',
      'Amortization', 'IO Period', 'Payment Frequency',
    ],
  },
  { id: 'fees', label: 'Fees & prepayment', rows: ['Origination Fee', 'Exit Fee', 'Prepayment'] },
]

/** Fields the dossier's metric bar edits on every tab: its header slots. One
 *  input per field on a screen — where the page prints one of these it says
 *  the bar is where it is edited, rather than drawing a second box for the
 *  same field. The value, LTC, cap rate and equity are no longer on the bar
 *  (Chris Young, 2 Nov), so they are edited here, where they are printed. */
const BAR_FIELDS: ReadonlySet<string> = HEADER_FIELDS

/** The catalogue field each term-sheet covenant row is read from, by the id
 *  capitalStackModel gives the `covenant_*` rows, and the deal figure its
 *  Current column restates. */
const COVENANT_FIELDS: Record<string, { field: string; current: string }> = {
  'max-ltv': { field: 'covenant_max_ltv', current: 'LTV' },
  'min-dscr': { field: 'covenant_min_dscr', current: 'DSCR' },
  'min-debt-yield': { field: 'covenant_min_debt_yield', current: 'debt yield' },
}

/** The term-sheet covenants a deal with none can be given here. */
const COVENANT_TARGETS: EditTarget[] = [
  { name: 'covenant_min_dscr', label: 'Min DSCR' },
  { name: 'covenant_max_ltv', label: 'Max LTV' },
  { name: 'covenant_min_debt_yield', label: 'Min Debt Yield' },
]

/** The future-funding leg the model draws beside a request whose commitment
 *  is larger than the loan. */
const FUTURE_FUNDING = 'future-funding-recorded'

/** The tranches the model draws from the deal's own request (its loan, and
 *  any future funding on it): their term is the deal's Loan Term. */
const REQUEST_LAYERS: ReadonlySet<string> = new Set(['senior-recorded', 'requested-recorded', FUTURE_FUNDING])

/** The deal fields the uses are read from when no schedule is stored: each
 *  one, entered, changes the Total Uses and the equity a stack closed by its
 *  uses implies. */
const USES_FIELDS = ['purchase_price', 'total_cost', 'closing_costs', 'interest_reserve', 'capex']

/** The uses line capitalStackModel adds for the part of a stated total cost
 *  the deal does not itemize: total cost less the itemized uses. */
const OTHER_COSTS = 'Other project costs (not itemized)'
const OTHER_COSTS_FORMULA = 'total project cost − the itemized uses'

const STORED_REASON = 'Read from the deal’s stored capital stack, schedule or loan structure, not a single deal field: no input here writes it.'
const STRUCTURE_COVENANT_REASON = 'A covenant on the loan’s structure record, not a deal field. No screen edits structure covenants yet, so it reads as stated.'

/** The stated inputs, by catalogue name, as edit targets. */
function capitalTargets(names: string[]): EditTarget[] {
  return [...new Set(names)].flatMap((name) => {
    const input = CAPITAL_INPUTS[name]
    return input ? [{ name: input.name, label: input.label, kind: input.kind }] : []
  })
}

function present(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

/**
 * How the implied equity was reached, in the stack's own terms: what closed
 * it (the uses, or the value) less ALL the debt it subtracts. The Sources
 * line said "total uses − total debt" on a refinance closed by value, where
 * no use is stated; and beside a stored senior and mezzanine the segment said
 * "purchase price − loan", which reads as $40M next to the $10M it labels.
 *
 * Closed by value, the model subtracts the debt at closing — future funding
 * is advanced later, so it is left out — and every other equity layer the
 * deal records. "value (as-is) − recorded debt" beside a $5M future-funding
 * leg read as $15M against the $20M it labelled, and beside a stored $5M
 * preferred equity as $20M against $15M.
 *
 * `whole`: the formula of the deal's equity in total — the residual with the
 * equity layers recorded beside it — which the bar's equity tile prints.
 */
function impliedEquityFormula(stack: CapitalStackModel, whole = false): string | undefined {
  const debt = stack.layers.filter((layer) => layer.isDebt)
  const loanOnly = debt.length === 1 && debt[0].amountField === 'loan_amount'
  switch (stack.residualSource) {
    case 'uses': {
      const less = loanOnly ? 'loan' : 'total debt'
      if (stack.usesBasis === 'the purchase price') return `purchase price − ${less}`
      if (stack.usesBasis === 'the total project cost') return `total project cost − ${less}`
      return `total uses − ${less}`
    }
    case 'property_value': {
      const funding = debt.some((layer) => layer.id === FUTURE_FUNDING)
      const others = !whole && otherEquityRecorded(stack)
      return `value (as-is) − ${funding ? 'debt at closing (future funding excluded)' : 'recorded debt'}${others ? ' − other recorded equity' : ''}`
    }
    case 'equity_field':
    case null:
      return undefined
    default: {
      const _exhaustive: never = stack.residualSource
      return _exhaustive
    }
  }
}

/** The inputs of a future-funding leg, by the formula the model gave it. */
function fundingInputs(formula: string | undefined): string[] | undefined {
  if (!formula) return undefined
  return formula.startsWith('total commitment')
    ? ['fully_funded_amount', 'loan_amount']
    : ['initial_funding', 'future_funding', 'loan_amount']
}

/** An equity layer the deal records beside the residual (a stored preferred
 *  or JV equity): the residual is what is left after it too. */
function otherEquityRecorded(stack: CapitalStackModel): boolean {
  return stack.layers.some((layer) => !layer.isDebt && layer.id !== 'residual-equity')
}

/**
 * The deal fields the implied equity moves with, by what closed the stack,
 * and the equity a sponsor may state in its place (a stated total equity
 * replaces the plug when no other equity layer is recorded).
 *
 * The leverage model's list named `total_equity` alone whenever a stored
 * equity layer made the total equity more than the residual — a field the
 * residual then ignores, so typing it moved nothing — and only the loan as
 * the debt and the price as the uses.
 */
function impliedEquityInputs(stack: CapitalStackModel): string[] {
  const debt = stack.layers.filter((layer) => layer.isDebt)
  const debtFields = debt.flatMap((layer) => (layer.amountField ? [layer.amountField] : []))
  const stated = otherEquityRecorded(stack) ? [] : ['total_equity']
  switch (stack.residualSource) {
    case 'uses': {
      // A stored schedule is read in place of the uses fields.
      const fromFields = stack.uses.some((row) => row.field === 'purchase_price' || row.field === 'total_cost')
      const funding = debt.some((layer) => layer.id === FUTURE_FUNDING) ? ['fully_funded_amount'] : []
      return [...(fromFields ? USES_FIELDS : []), ...debtFields, ...funding, ...stated]
    }
    case 'property_value':
      // The debt at closing: a future advance changes neither the value nor it.
      return ['property_value', ...debtFields, ...stated]
    case 'equity_field':
    case null:
      return []
    default: {
      const _exhaustive: never = stack.residualSource
      return _exhaustive
    }
  }
}

/**
 * One value the page prints, unlocked:
 *   - a catalogue field is its input, in place of the text;
 *   - a figure this page computes (`computed`) opens the inputs it is made of;
 *   - a field another place on this screen already edits — the metric bar, or
 *     `editedAt` — reads as it does locked and says where (one input per field
 *     on a screen); an engine-derived field opens its inputs either way;
 *   - a value no field holds reads as locked, with why.
 * Locked, it is exactly the marks and text the cell drew before.
 *
 * A stated field's state and Sponsor marks stay beside its input until a
 * person types over it, when they would describe a value no longer shown.
 * The input used to drop them, so unlocking took the Sponsor tag off the
 * sponsor model's purchase price on the Uses line — the screening
 * workflow's distinction, which the line kept while the Inputs block sat
 * under it.
 */
function CapitalValue({
  field,
  label,
  display,
  marks = null,
  computed,
  inputs,
  editedAt,
  also,
  reason = STORED_REASON,
}: {
  /** The catalogue field the value IS, when one holds it. */
  field?: string
  label: string
  display: string
  /** The state chips drawn before the value. */
  marks?: ReactNode
  /** The page computed the value: its formula. */
  computed?: string
  inputs?: string[]
  /** Where on this screen the field is edited instead ("under Sources & Uses"). */
  editedAt?: string
  /** Inputs the row's figure also takes that nothing else on the screen
   *  offers (the loan's fully funded amount): a value edited elsewhere opens
   *  them, so a blank that drives the tab is entered on it. */
  also?: EditTarget[]
  /** Why a value no field holds cannot be edited. */
  reason?: string
}) {
  const edit = useDossierEdit()
  const locked = <>{marks}{display}</>
  // A dash is no value: nothing to edit and nothing to explain.
  if (display === '—' && (computed !== undefined || !field)) return locked
  const name = field ?? `capital:${label}`
  if (computed !== undefined) {
    return (
      <EditableValue name={name} label={label} display={display} marks={marks} marksFirst computed={computed} inputs={inputs?.filter((n) => n !== field)}>
        {locked}
      </EditableValue>
    )
  }
  const elsewhere = field && outputFormulaOf(edit, field) === null
    ? editedAt ?? (BAR_FIELDS.has(field) ? 'on the metric bar above' : undefined)
    : undefined
  if (elsewhere && also && writableTargets(edit, also).length > 0) {
    return (
      <>
        {marks}
        <InputsTrigger
          targets={also}
          label={`${label} · ${also.map((target) => target.label).join(', ')}`}
          title={`Edited ${elsewhere}: one input per field on a screen. Open to enter ${also.map((target) => target.label).join(', ')}.`}
        >
          {display}
        </InputsTrigger>
      </>
    )
  }
  if (!field || elsewhere) {
    return (
      <EditableValue
        name={name}
        label={label}
        display={display}
        marks={marks}
        marksFirst
        fields={[]}
        readOnlyReason={elsewhere ? `Edited ${elsewhere}: one input per field on a screen.` : reason}
      >
        {locked}
      </EditableValue>
    )
  }
  if (marksBesideInput(edit, [{ name: field, label }], marks)) {
    return (
      <>
        {marks}
        <EditableValue name={field} label={label} display={display} />
      </>
    )
  }
  return (
    <EditableValue name={field} label={label} display={display} marks={marks} marksFirst>
      {locked}
    </EditableValue>
  )
}

/** The targets a save can write. */
function writableTargets(edit: DossierEdit, targets: EditTarget[]): EditTarget[] {
  return targets.filter((target) => !isSystemField(target.name) && isWritableField(target.name, edit.defs))
}

/** True while an input stands in for a stated value nobody has typed over:
 *  its marks are drawn beside the box, as they are beside the text. */
function marksBesideInput(edit: DossierEdit, fields: EditTarget[], marks: ReactNode, computed?: string): boolean {
  if (marks == null || fields.length === 0) return false
  if (fields.some((target) => Object.prototype.hasOwnProperty.call(edit.draft, target.name))) return false
  return valueEditMode(edit, fields[0].name, { fields, computed }).mode === 'input'
}

/**
 * A phrase naming what is missing ("DSCR needs NOI"). Unlocked, it opens
 * those inputs where it is read, so a blank is filled on this tab without a
 * block of inputs growing the panel. Locked, it is the phrase it was.
 *
 * A plain inline span with the edit hairline in its own negative margin, not
 * EditableValue's composite (an inline-flex box): inside a sentence each
 * phrase then wraps as the text does locked, so the line keeps its height.
 */
function InputsTrigger({
  targets,
  label,
  title,
  children,
}: {
  targets: EditTarget[]
  label: string
  /** The hover, when the phrase is a value edited elsewhere. */
  title?: string
  children: ReactNode
}) {
  const edit = useDossierEdit()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const writable = writableTargets(edit, targets)
  if (!edit.unlocked || writable.length === 0) return <>{children}</>
  const open = (target: HTMLElement) => setAnchor((current) => (current ? null : target))
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        data-edit-composite={targets[0].name}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        title={title ?? `Enter ${targets.map((target) => target.label).join(', ')}`}
        onClick={(event) => open(event.currentTarget)}
        onKeyDown={activateOnKey(open)}
        className="dossier-edit-trigger"
      >
        {children}
      </span>
      {anchor ? (
        <EditPopover anchor={anchor} title={label} onClose={() => setAnchor(null)}>
          <InputEditorList
            names={targets.map((target) => target.name)}
            labels={Object.fromEntries(targets.map((target) => [target.name, target.label]))}
            renderInput={renderCatalogueInput}
          />
        </EditPopover>
      ) : null}
    </>
  )
}

/** A figure the metric bar shows, from the leverage model, keeping its state:
 *  a computed one names its formula (the tile says Derived), an implied one
 *  says Implied, a stated catalogue field keeps its own lineage state.
 *
 *  A computed or implied tile carries the inputs the page computed it from.
 *  Without them the bar fell back to the catalogue's rule, which is not always
 *  this page's arithmetic: Washington Square's LTC is loan ÷ purchase price,
 *  and its tile offered Loan Amount and Total Project Cost — the price it is
 *  built on was not offered, and Loan / unit offered nothing at all.
 *
 *  An implied figure with a formula is the page's arithmetic too, so it is
 *  passed as derived as well: the tile says Derived with Implied beside it
 *  (the bar's convention for an implied total) and, unlocked, opens its
 *  inputs. The bar counts only a `derived` page figure as computed, so the
 *  Implied Equity tile was a plain source-details button with none. */
function metricOf(
  figure: LeverageFigure,
  over: {
    id?: string
    label?: string
    icon?: DossierMetric['icon']
    note?: string
    value?: string
    /** The page's own formula and inputs, where it states them better. */
    formula?: string
    inputs?: string[]
    implied?: boolean
  } = {},
): DossierMetric {
  const id = over.id ?? figure.id
  const formula = over.formula ?? figure.formula
  const implied = over.implied ?? figure.implied ?? false
  const derived = implied
    ? formula
    : figure.basis === 'derived' && figure.field !== id ? formula ?? 'computed from stated figures' : undefined
  return {
    id,
    label: over.label ?? figure.label,
    value: over.value ?? figure.display,
    icon: over.icon,
    fieldValue: !implied && figure.basis === 'stated' && figure.field === id ? true : undefined,
    derived,
    implied: implied || undefined,
    inputs: derived || implied ? (over.inputs ?? figure.inputs).filter((name) => name !== id && name !== figure.id) : undefined,
    note: over.note,
    detail: figure.operands
      ? [{ label: 'Made of', value: figure.operands }]
      : figure.waiting
        ? [{ label: 'Waiting on', value: figure.waiting }]
        : undefined,
  }
}

function leverageMetrics(model: LeverageModel): DossierMetric[] {
  const f = model.figures
  // `ltv` and `dscr` merge onto the header's own slots (see MetricBar), which
  // print whether or not the page has a figure for them. The page's LTC, debt
  // yield, cap rate, per-unit basis and equity are read in its Coverage &
  // Sizing and Capitalization panels: the bar is one definition on every tab
  // (Chris Young, 2 Nov), and its debt yield is the header's own, withheld on
  // a note whose senior debt is not stated as it is everywhere else.
  return [
    metricOf(f.ltv, { icon: Scale, note: 'The loan against the as-is value of the asset.' }),
    metricOf(f.dscr, { icon: Calculator, note: 'In-place NOI against annual debt service.' }),
  ]
}

export function CapitalStackPage({
  raw,
  loanId,
  activity,
  notes,
  documents: documentsProp,
  onGoto,
}: {
  raw: RawDealFile
  defs: DealFieldDef[]
  loanId: string
  activity: LoanActivityEvent[]
  notes: { id: string; body: string; author: string; createdAt: string }[]
  documents: DealDocument[]
  onGoto?: (tab: DealDossierTab) => void
}) {
  const edit = useDossierEdit()
  const { unlocked } = edit
  const structureQuery = useQuery({
    queryKey: ['loan', loanId, 'structure'],
    queryFn: () => structuresService.get(loanId),
    enabled: Boolean(loanId),
    retry: false,
    refetchInterval: 30_000,
  })
  // The deal's lineage — the same cached read the dossier makes — so a figure
  // the engine derived is said to be derived here too.
  const lineageQuery = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldLineage(loanId),
    queryFn: () => loansService.getFieldLineage(loanId),
    enabled: Boolean(loanId),
    retry: false,
  })
  const { stack, leverage, sourceOf } = useCapitalization({
    raw,
    loanId,
    structure: structureQuery.data ?? null,
    // The prop, not a second fetch of the same key. The parent already reads
    // this list and merges the deal's intake originals into it; a private
    // refetch shadowed that merge (nd: "No documents attached" on a deal
    // holding three).
    documents: documentsProp,
    activity,
    notes,
    lineage: lineageQuery.data?.fields,
  })
  // The loan's terms as the Economics Financing Summary reads them — one
  // reader for the terms, so the two tabs can never print different ones.
  const economics = useEconomicsModel({ raw, loanId, activity, notes })

  const metrics = useMemo(() => leverageMetrics(leverage), [leverage])
  useProvideDossierMetrics('capital_stack', metrics)

  // A stack closed by value that draws future funding totals the value AND
  // the advances made after closing. The leverage model labels that total
  // "Value (As-Is)" with the value's field and marks, and its attachments
  // LTV: $45,000,000 printed as the value of a $40M property, the senior at
  // 0–44.4% "LTV" beside a 50.0% LTV on the bar. Here it is what it is, the
  // tranches added, and the attachment is a place in the stack.
  const beyondValue =
    leverage.capitalization != null && stack.residualSource === 'property_value' && stack.layers.some((layer) => layer.id === FUTURE_FUNDING)
  const cap = leverage.capitalization && beyondValue
    ? { ...leverage.capitalization, totalLabel: 'Total Capitalization', ratio: null, totalField: undefined }
    : leverage.capitalization
  const sizing = SIZING_FIGURES.map((id) => leverage.figures[id])
  const sized = sizing.filter((figure) => figure.basis != null)
  const waitingSizing = sizing.filter((figure) => figure.basis == null && figure.waiting)
  // A column every tranche leaves blank is a column of dashes saying nothing:
  // it is left out, and the Loan Terms panel names the unstated rate and term.
  const showLender = stack.layers.some((layer) => layer.lender)
  const showRate = stack.layers.some((layer) => layer.isDebt && rateLabel(layer.rate, layer.rateLabel) !== '—')
  const showTerm = stack.layers.some((layer) => layer.term)
  const sponsorEquity = sponsorEquityFormula({
    residualSource: stack.residualSource,
    equityDerived: stack.equityDerived,
    equityFormula: lineageQuery.data?.fields.find((f) => f.fieldName === 'total_equity')?.derivation?.formula,
  })
  const terms = economics.financing.filter((row) => TERM_ROWS.includes(row.label))
  const statedTerms = terms.filter((row) => financingDisplay(row) !== '—' || row.held)
  const unstatedTerms = terms.filter((row) => financingDisplay(row) === '—' && !row.held)
  const plugged = stack.sources.some((row) => row.implied)
  const impliedFormula = impliedEquityFormula(stack)
  const equityInputs = impliedEquityInputs(stack)
  // What the Capitalization's waiting line asks for: the stated inputs the
  // blank leverage, coverage and equity figures are made of, still empty on
  // the deal. A note whose senior debt is unstated waits on no field the deal
  // has.
  const waitingTargets = capitalTargets(
    stack.seniorUnstated
      ? []
      : (['ltv', 'ltc', 'debt_yield', 'dscr', 'equity'] as const)
          .flatMap((id) => (leverage.figures[id].basis == null ? leverage.figures[id].inputs : []))
          .filter((name) => !present(edit.effectiveValue(name))),
  )
  // Blank inputs a figure on this tab takes that no line here names once the
  // Inputs blocks are gone: the commitment that draws a future-funding leg
  // beside the loan, and the GP / LP split of a stated sponsor equity (the
  // commitments line prints only once one is stated). The commitment is a
  // header slot now (Fully Funded Loan Amount), entered on the bar, so it is
  // not offered a second time here.
  const fundingTargets = stack.layers.some((layer) => layer.id === FUTURE_FUNDING)
    ? []
    : capitalTargets(['fully_funded_amount'].filter((name) => !BAR_FIELDS.has(name)))
  const commitmentTargets = stack.gpEquity == null && stack.lpEquity == null ? capitalTargets(['gp_equity', 'lp_equity']) : []
  // The uses fields the Total Uses is read from, the blank ones included: a
  // closing cost, reserve or capex budget entered changes it and the plug.
  const usesInputs = stack.uses.some((row) => row.field === 'purchase_price' || row.field === 'total_cost')
    ? USES_FIELDS
    : stack.uses.flatMap((row) => (row.field ? [row.field] : []))
  // Sources assembled from the stack (no stored schedule) are its layers, one
  // for one: a future-funding leg is the model's arithmetic and says so here
  // too, as it does in the Capitalization table.
  const sourcesFromLayers =
    stack.sources.length === stack.layers.length &&
    stack.sources.every((row, i) => row.label === stack.layers[i].position && row.amount === stack.layers[i].amount)
  // The not-itemized costs line is the model's own remainder only when the
  // uses were read from the deal's fields (a stored schedule line has none).
  const usesFromFields = stack.uses.some((row) => row.field === 'purchase_price')

  // The gauges drawn beside the sized figures: the same numbers, on a scale.
  const gauges = figureGauges(leverage.figures, stack.covenants)
  // The loan's terms by group, in the order a term sheet reads them. A group
  // with nothing stated draws no rows; its unstated terms are named once.
  const termGroups = TERM_GROUPS.map((group) => ({
    ...group,
    stated: group.rows.flatMap((label) => statedTerms.filter((row) => row.label === label)),
    unstated: group.rows.flatMap((label) => unstatedTerms.filter((row) => row.label === label)),
  }))
  // Many unstated terms are laid out by group in the room the few stated ones
  // leave; a few are one line under the rows. "Many" was six of the sixteen
  // terms; the four date terms added on 26 Oct 2026 (Closing Date, Maturity,
  // Extension Options, Extension Maturity) raise it to eight of twenty, so a deal
  // that states what it stated before does not change layout for them.
  const termsOutline = unstatedTerms.length >= 8
  // A source drawn from the stack takes the color of its tranche in the bar;
  // with no bar drawn, debt is the debt blue and equity the equity amber.
  const sourceColor = (index: number): string | undefined => {
    if (!sourcesFromLayers) return undefined
    const drawn = cap?.segments[index]?.color
    if (drawn) return drawn
    return stack.layers[index]?.isDebt ? DEBT_STEPS[0] : EQUITY_STEPS[0]
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-hidden">
      <div className="dossier-capital-grid grid min-h-0 flex-1 auto-rows-min gap-4 overflow-y-auto xl:grid-cols-12">
        <DossierPanel title="Capitalization" dense className="min-w-0 xl:col-span-7 min-[1440px]:col-span-6 min-[1700px]:col-span-7">
          <div data-testid="capitalization-panel" className="cs-panel-gap flex min-h-0 flex-1 flex-col gap-2">
            {cap ? (
              <>
                <CapitalizationBar capitalization={cap} size="hero" />
                {/* The table takes the height the bar leaves: its rows grow
                    with the panel, the header and the total keep their own.
                    shrink-0: a scroll box in a flex column may shrink to
                    nothing. Unlocked, each amount is edited in its own cell,
                    so the table keeps the height it has locked. */}
                <div className="min-h-0 overflow-x-auto" style={{ flex: '2 0 auto' }}>
                  <table data-panel-table className="h-full w-full min-w-[560px] text-left text-[10px]">
                    <thead className="text-[9px] uppercase tracking-wider text-stone-600">
                      <tr className="h-px">
                        <th className="px-1 py-1 !text-left font-medium">Tranche</th>
                        {showLender ? <th className="px-1 py-1 !text-left font-medium">Lender / Investor</th> : null}
                        <th className="px-1 py-1 !text-right font-medium">Amount</th>
                        <th className="px-1 py-1 !text-right font-medium">% of Total</th>
                        <th className="px-1 py-1 !text-right font-medium">{cap.ratio ? `Attachment (${cap.ratio})` : 'Attachment'}</th>
                        {leverage.perNoun ? <th className="px-1 py-1 !text-right font-medium">Per {leverage.perNoun}</th> : null}
                        {showRate ? <th className="px-1 py-1 !text-right font-medium">Rate</th> : null}
                        {showTerm ? <th className="px-1 py-1 !text-right font-medium">Term</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {cap.segments.map((segment) => {
                        // The implied equity says how the stack closed, in
                        // the page's words (see impliedEquityFormula).
                        const amountFormula = segment.isImplied ? impliedFormula : segment.amountFormula
                        return (
                        <tr
                          key={segment.id}
                          data-tranche={segment.id}
                          className={`border-t text-stone-300 ${segment.isRequested ? 'border-amber-300/20 bg-amber-300/[0.05]' : 'border-white/[0.04]'}`}
                        >
                          <td className="px-1 py-1">
                            <span className="inline-flex items-center gap-1.5">
                              <Swatch color={segment.color} implied={segment.isImplied} />
                              <span>{segment.label}</span>
                              {segment.isRequested ? <RequestTag /> : null}
                            </span>
                          </td>
                          {showLender ? (
                            <td className="max-w-[9rem] truncate px-1 py-1 text-stone-400">
                              <CapitalValue label={`${segment.label} lender`} display={segment.lender ?? '—'} />
                            </td>
                          ) : null}
                          <td className="px-1 py-1 text-right font-mono text-stone-100">
                            <span className="inline-flex items-center justify-end gap-1">
                              <CapitalValue
                                field={segment.amountField}
                                label={segment.label}
                                display={moneyExact(segment.amount)}
                                marks={<AmountMarks amountField={segment.amountField} amountFormula={amountFormula} isImplied={segment.isImplied} sourceOf={sourceOf} />}
                                computed={segment.isImplied ? impliedFormula ?? '' : segment.amountFormula}
                                inputs={segment.isImplied ? equityInputs : fundingInputs(segment.amountFormula)}
                                also={
                                  segment.id === 'senior-recorded'
                                    ? fundingTargets
                                    : segment.amountField === 'total_equity'
                                      ? commitmentTargets
                                      : undefined
                                }
                              />
                            </span>
                          </td>
                          <td className="px-1 py-1 text-right font-mono text-stone-400">{segment.share.toFixed(1)}%</td>
                          {/* Where the tranche sits in the stack, as the range
                              a lender quotes and as a track under it. */}
                          <td className="px-1 py-1 text-right font-mono text-stone-200">
                            <div className="flex flex-col items-end gap-1">
                              <span>{attachmentRange(segment)}</span>
                              <AttachmentTrack segment={segment} />
                            </div>
                          </td>
                          {leverage.perNoun ? <td className="px-1 py-1 text-right font-mono text-stone-400">{segment.perUnit ?? '—'}</td> : null}
                          {showRate ? (
                            <td className="px-1 py-1 text-right font-mono text-stone-400">
                              {segment.isDebt ? (
                                <span className="inline-flex items-center justify-end gap-1">
                                  <CapitalValue
                                    field={segment.rateField}
                                    label={`${segment.label} rate`}
                                    display={rateLabel(segment.rate, segment.rateLabel)}
                                    marks={segment.rateLabel ? <AmountMarks amountField={segment.rateField} sourceOf={sourceOf} /> : null}
                                    editedAt="under Loan Terms & Pricing"
                                  />
                                </span>
                              ) : '—'}
                            </td>
                          ) : null}
                          {showTerm ? (
                            <td className="whitespace-nowrap px-1 py-1 text-right text-stone-400">
                              <CapitalValue
                                field={REQUEST_LAYERS.has(segment.id) && segment.term ? 'term_months' : undefined}
                                label={`${segment.label} term`}
                                display={segment.term ?? '—'}
                                editedAt="under Loan Terms & Pricing"
                              />
                            </td>
                          ) : null}
                        </tr>
                        )
                      })}
                      <tr className="h-px border-t border-amber-400/20 font-medium text-stone-100">
                        <td className="px-1 py-1.5" colSpan={showLender ? 2 : 1}>
                          {cap.totalLabel}{cap.totalImplied ? ' (implied)' : ''}
                        </td>
                        <td className="px-1 py-1.5 text-right font-mono">
                          <span className="inline-flex items-center justify-end gap-1">
                            {/* The price or cost the stack is closed against
                                is a use, edited with the uses; the value is
                                the metric bar's. A total no field holds is the
                                tranches above, added. */}
                            <CapitalValue
                              field={cap.totalField}
                              label={cap.totalLabel}
                              display={moneyExact(cap.total)}
                              marks={cap.totalField ? <AmountMarks amountField={cap.totalField} sourceOf={sourceOf} /> : null}
                              computed={cap.totalField ? undefined : 'the sum of the tranches above'}
                              inputs={cap.segments.flatMap((segment) => (segment.amountField ? [segment.amountField] : []))}
                              editedAt={cap.totalField === 'purchase_price' || cap.totalField === 'total_cost' ? 'under Sources & Uses' : undefined}
                            />
                          </span>
                        </td>
                        <td className="px-1 py-1.5 text-right font-mono">100.0%</td>
                        <td />
                        {leverage.perNoun ? <td className="px-1 py-1.5 text-right font-mono text-stone-400">{cap.totalPerUnit ?? '—'}</td> : null}
                        {showRate ? <td /> : null}
                        {showTerm ? <td /> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : leverage.tranches.length > 0 ? (
              <div className="shrink-0 overflow-x-auto">
              <table data-panel-table className="w-full text-left text-[10px]">
                <thead className="text-[9px] uppercase tracking-wider text-stone-600">
                  <tr>
                    <th className="px-1 py-1 !text-left font-medium">Tranche</th>
                    <th className="px-1 py-1 !text-right font-medium">Amount</th>
                    {leverage.perNoun ? <th className="px-1 py-1 !text-right font-medium">Per {leverage.perNoun}</th> : null}
                    {showRate ? <th className="px-1 py-1 !text-right font-medium">Rate</th> : null}
                    {showTerm ? <th className="px-1 py-1 !text-right font-medium">Term</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {leverage.tranches.map((layer) => (
                    <tr key={layer.id} data-tranche={layer.id} className="border-t border-white/[0.04] text-stone-300">
                      <td className="px-1 py-1.5">
                        <span className="inline-flex items-center gap-1.5">
                          <Swatch color="#3987e5" />
                          {layer.position}
                          {layer.isRequested ? <RequestTag /> : null}
                        </span>
                      </td>
                      <td className="px-1 py-1.5 text-right font-mono text-stone-100">
                        <span className="inline-flex items-center justify-end gap-1">
                          <CapitalValue
                            field={layer.amountField}
                            label={layer.position}
                            display={moneyExact(layer.amount)}
                            marks={<AmountMarks amountField={layer.amountField} amountFormula={layer.amountDerived} isImplied={layer.isImplied} sourceOf={sourceOf} />}
                            computed={layer.amountDerived}
                            inputs={fundingInputs(layer.amountDerived)}
                            also={layer.id === 'senior-recorded' ? fundingTargets : undefined}
                          />
                        </span>
                      </td>
                      {leverage.perNoun ? <td className="px-1 py-1.5 text-right font-mono text-stone-400">{layer.perUnit ?? '—'}</td> : null}
                      {showRate ? (
                        <td className="px-1 py-1.5 text-right font-mono text-stone-400">
                          <span className="inline-flex items-center justify-end gap-1">
                            <CapitalValue
                              field={layer.rateField}
                              label={`${layer.position} rate`}
                              display={rateLabel(layer.rate, layer.rateLabel)}
                              marks={layer.rateLabel ? <AmountMarks amountField={layer.rateField} sourceOf={sourceOf} /> : null}
                              editedAt="under Loan Terms & Pricing"
                            />
                          </span>
                        </td>
                      ) : null}
                      {showTerm ? (
                        <td className="whitespace-nowrap px-1 py-1.5 text-right text-stone-400">
                          <CapitalValue
                            field={REQUEST_LAYERS.has(layer.id) && layer.term ? 'term_months' : undefined}
                            label={`${layer.position} term`}
                            display={layer.term ?? '—'}
                            editedAt="under Loan Terms & Pricing"
                          />
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : null}
            {leverage.waiting && !cap ? (
              leverage.tranches.length ? (
                // Where the bar will be drawn: the tranches are listed, and the
                // stack waits on the one input named here. No share is drawn of
                // a whole nobody has stated.
                <FillEmpty>
                  <div className="font-mono text-[8px] uppercase tracking-wider text-stone-600">Capital stack not drawn</div>
                  <div data-testid="capitalization-waiting" className="mt-1 max-w-md text-[10.5px] text-stone-400">
                    {/* Unlocked, the line opens the empty inputs it waits on. */}
                    <InputsTrigger targets={waitingTargets} label="Leverage and coverage">{leverage.waiting}</InputsTrigger>
                  </div>
                </FillEmpty>
              ) : (
                <FillEmpty>
                  <div className="font-mono text-[8px] uppercase tracking-wider text-stone-600">Capital stack not drawn</div>
                  <div data-testid="capitalization-waiting" className="mt-1">
                    <InputsTrigger targets={waitingTargets} label="Leverage and coverage">{leverage.waiting}</InputsTrigger>
                  </div>
                </FillEmpty>
              )
            ) : null}
            {stack.gpEquity != null || stack.lpEquity != null ? (
              <p className="shrink-0 text-[10px] text-stone-400">
                Equity commitments: GP <CapitalValue field="gp_equity" label="GP Equity" display={formatAmount(stack.gpEquity)} />
                {stack.gpPct != null ? ` (${stack.gpPct.toFixed(1)}%)` : ''} · LP <CapitalValue field="lp_equity" label="LP Equity" display={formatAmount(stack.lpEquity)} />
                {stack.lpPct != null ? ` (${stack.lpPct.toFixed(1)}%)` : ''}
              </p>
            ) : null}
            {stack.equityCommitmentGap != null ? (
              <p className="shrink-0 text-[9px] text-amber-200/80">
                GP + LP commitments come to {formatAmount((stack.gpEquity ?? 0) + (stack.lpEquity ?? 0))},{' '}
                {formatAmount(Math.abs(stack.equityCommitmentGap))} {stack.equityCommitmentGap > 0 ? 'short of' : 'over'} the total equity —{' '}
                {equityGapBasis(stack.equityDerived ? sponsorEquity : undefined)}
              </p>
            ) : null}
            {/* The stack's own note says what closed it and what is implied.
                On a loan-only deal the waiting line above already says what
                is missing, so the note is kept for the cases that need it: a
                drawn stack, a note behind unstated senior debt, or a stated
                LTV that (rule R3) implies no equity. It sits at the panel's
                foot. */}
            {stack.layers.length > 0 && (cap || stack.seniorUnstated || stack.ltv != null) ? (
              <p className="mt-auto shrink-0 text-[9px] leading-relaxed text-stone-500">{stack.stackDisclosure}</p>
            ) : null}
          </div>
        </DossierPanel>

        <DossierPanel title="Coverage & Sizing" dense className="min-w-0 xl:col-span-5 min-[1440px]:col-span-6 min-[1700px]:col-span-5">
          <div data-testid="coverage-sizing" className="cs-container flex min-h-0 flex-1 flex-col gap-2">
            {sized.length > 0 ? (
              // Each row is a figure the table states, with the same figure on
              // its scale beside it. The rows take the height the panel has.
              <div className="min-h-0 shrink-0" style={{ flex: `${sized.length} 0 auto` }}>
                <table data-panel-table className="h-full w-full text-left text-[10px]">
                  <thead className="text-[9px] uppercase tracking-wider text-stone-600">
                    <tr className="h-px">
                      <th className="px-1 py-1 !text-left font-medium">Measure</th>
                      <th className="px-1 py-1 !text-right font-medium">Value</th>
                      <th className="cs-gauge-col px-1.5 py-1 !text-left font-medium"><span className="sr-only">Scale</span></th>
                      <th className="px-1 py-1 !text-left font-medium">How it is reached</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sized.map((figure) => {
                      const gauge = gauges[figure.id as GaugeId]
                      return (
                        <tr key={figure.id} data-figure={figure.id} className="border-t border-white/[0.04] text-stone-300">
                          <td className="whitespace-nowrap px-1 py-1">
                            <span className="inline-flex items-center gap-1">
                              {figure.label}
                              <FigureMarks figure={figure} sourceOf={sourceOf} />
                            </span>
                          </td>
                          {/* Unlocked, a derived ratio opens the inputs this page
                              computed it from; a stated one is the metric bar's
                              field, edited there. A stated ratio's operands open
                              theirs, so its NOI or debt service is editable here
                              even when every ratio is stated. */}
                          <td className="whitespace-nowrap px-1 py-1 text-right font-mono text-stone-100">
                            <CapitalValue
                              field={figure.basis === 'stated' ? figure.field : undefined}
                              label={figure.label}
                              display={figure.display}
                              computed={figure.basis === 'derived' ? figure.formula ?? '' : undefined}
                              inputs={figure.inputs.filter((name) => name !== figure.id)}
                            />
                          </td>
                          <td className="cs-gauge-col w-[26%] px-1.5 py-1">
                            {gauge ? <GaugeBar id={figure.id as GaugeId} gauge={gauge} state={gaugeStateOf(figure, sourceOf(figure.field)?.state)} /> : null}
                          </td>
                          <td className="px-1 py-1 text-[9.5px] text-stone-500">
                            {figure.basis === 'derived' ? (
                              `${figure.operands ?? figure.formula ?? 'computed'}${figure.operands && figure.formula ? ` (${figure.formula})` : ''}`
                            ) : figure.operands ? (
                              <>
                                As stated ·{' '}
                                <InputsTrigger
                                  targets={capitalTargets(figure.inputs.filter((name) => name !== figure.id && name !== figure.field))}
                                  label={`${figure.label} operands`}
                                >
                                  {figure.operands}
                                </InputsTrigger>
                              </>
                            ) : 'As stated'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <FillEmpty>
                <div className="font-mono text-[8px] uppercase tracking-wider text-stone-600">Nothing to size yet</div>
                <div className="mt-1">{leverage.waiting ?? 'No figure on this deal supports a coverage or sizing ratio yet.'}</div>
              </FillEmpty>
            )}
            {sized.length > 0 && waitingSizing.length > 0 ? (
              // What the blank figures wait on, one line each. Unlocked, each
              // opens the inputs it names, in place: the Inputs block that sat
              // under this table made the panel body scroll at 1366x768 (and
              // repeated the bar's fields).
              <div
                data-testid="sizing-waiting"
                className="flex min-h-0 shrink-0 flex-col rounded border border-dashed border-white/[0.08] px-2 py-1"
                style={{ flex: `${Math.max(1, waitingSizing.length * 0.75)} 0 auto` }}
              >
                <div className="font-mono text-[8px] uppercase tracking-wider text-stone-600">Not shown yet</div>
                <ul className="flex flex-1 flex-col">
                  {waitingSizing.map((figure) => (
                    <li key={figure.id} data-pending={figure.id} className="flex flex-1 items-center gap-1.5 border-t border-white/[0.04] text-[9.5px] text-stone-500 first:border-t-0">
                      <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full border border-stone-600" />
                      <span>
                        <InputsTrigger targets={capitalTargets(figure.inputs)} label={figure.label}>
                          <span className="text-stone-400">{figure.label.replace(/ \/ unit$/, ' per unit')}</span>{' '}
                          <span>{figure.waiting}</span>
                        </InputsTrigger>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </DossierPanel>

        <DossierPanel title="Sources & Uses" dense className="min-w-0 xl:col-span-4">
          <div className="flex min-h-0 flex-1 flex-col gap-2">
          {stack.suTemplate ? (
            <div className="font-mono text-[8px] uppercase tracking-widest text-amber-200/60">
              {stack.suTemplate} template
            </div>
          ) : null}
          {stack.sources.length === 0 && stack.uses.length === 0 ? (
            <FillEmpty>
              <InputsTrigger targets={capitalTargets(['loan_amount', 'purchase_price', 'total_cost'])} label="Sources & Uses">
                No sources & uses schedule is stored on this deal, and no loan or cost is recorded to assemble one.
              </InputsTrigger>
            </FillEmpty>
          ) : (
            // Two columns that share one height: each line takes its share of
            // it, with its size against the column's total under it, and the
            // two totals sit on the same line at the foot.
            <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 text-[10px]">
              <div className="flex min-h-0 flex-col">
                <div className="mb-1 text-[9px] uppercase tracking-wider text-stone-600">Sources</div>
                <ul className="flex flex-1 flex-col">
                  {stack.sources.map((row, index) => {
                    // The equity plug is said in the stack's own terms (it was
                    // "total uses − total debt" on a stack closed by value);
                    // a future-funding leg is the model's arithmetic.
                    const legFormula = !row.implied && !row.field && sourcesFromLayers ? stack.layers[index].amountDerived : undefined
                    const formula = row.implied ? impliedFormula : legFormula
                    const share = shareOf(row.amount, stack.sourceTotal)
                    return (
                      <li key={row.label} className="flex min-h-[24px] max-h-[68px] flex-1 flex-col justify-center gap-1 border-t border-white/[0.04] text-stone-300 first:border-t-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate text-stone-400">{row.label}</span>
                          <span className="inline-flex shrink-0 items-center gap-1 font-mono">
                            <CapitalValue
                              field={row.field}
                              label={row.label}
                              display={moneyExact(row.amount)}
                              marks={row.implied || row.field || formula ? (
                                <AmountMarks isImplied={row.implied} amountField={row.field} amountFormula={formula} sourceOf={sourceOf} />
                              ) : null}
                              computed={row.implied ? impliedFormula ?? '' : legFormula}
                              inputs={row.implied ? equityInputs : fundingInputs(legFormula)}
                            />
                          </span>
                        </div>
                        {share != null ? <ShareBar share={share} color={sourceColor(index)} implied={row.implied} /> : null}
                      </li>
                    )
                  })}
                </ul>
                <div className="mt-1.5 flex justify-between border-t border-white/[0.06] pt-1 font-medium text-stone-100">
                  <span>{stack.sourceTotalLabel}</span>
                  <span className="font-mono">
                    <CapitalValue
                      label={stack.sourceTotalLabel}
                      display={moneyExact(stack.sourceTotal)}
                      computed={stack.sourcesStatedTotal ? undefined : 'the sum of the sources above'}
                      inputs={stack.sources.flatMap((row) => (row.field ? [row.field] : []))}
                    />
                  </span>
                </div>
              </div>
              <div className="flex min-h-0 flex-col">
                <div className="mb-1 text-[9px] uppercase tracking-wider text-stone-600">Uses</div>
                {stack.uses.length === 0 && !stack.usesStatedTotal ? (
                  <FillEmpty>
                    <span className="text-[10px] text-stone-500">
                      No uses are stated —{' '}
                      <InputsTrigger targets={capitalTargets(['purchase_price', 'total_cost'])} label="Uses">add a purchase price or project cost</InputsTrigger>.
                    </span>
                  </FillEmpty>
                ) : (
                  <>
                    {/* The price and costs are edited here, in their own
                        lines: the uses own them on this screen. */}
                    <ul className="flex flex-1 flex-col">
                      {stack.uses.map((row) => {
                        const remainder = usesFromFields && !row.field && row.label === OTHER_COSTS
                        const share = stack.usesComplete ? shareOf(row.amount, stack.useTotal) : null
                        return (
                          <li key={row.label} className="flex min-h-[24px] max-h-[68px] flex-1 flex-col justify-center gap-1 border-t border-white/[0.04] text-stone-300 first:border-t-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="min-w-0 truncate text-stone-400">{row.label}</span>
                              <span className="inline-flex shrink-0 items-center gap-1 font-mono">
                                <CapitalValue
                                  field={row.field}
                                  label={row.label}
                                  display={moneyExact(row.amount)}
                                  marks={row.field || remainder ? <AmountMarks amountField={row.field} amountFormula={remainder ? OTHER_COSTS_FORMULA : undefined} sourceOf={sourceOf} /> : null}
                                  computed={remainder ? OTHER_COSTS_FORMULA : undefined}
                                  inputs={remainder ? ['total_cost', 'purchase_price', 'closing_costs', 'interest_reserve', 'capex'] : undefined}
                                />
                              </span>
                            </div>
                            {share != null ? <ShareBar share={share} /> : null}
                          </li>
                        )
                      })}
                    </ul>
                    <div className="mt-1.5 flex justify-between border-t border-white/[0.06] pt-1 font-medium text-stone-100">
                      <span>Total Uses</span>
                      <span className="font-mono">
                        {stack.usesComplete ? (
                          <CapitalValue
                            label="Total Uses"
                            display={moneyExact(stack.useTotal)}
                            // The schedule's own Total Uses is read as stated,
                            // never re-added to the lines it sums.
                            computed={stack.usesStatedTotal ? undefined : 'the sum of the uses above'}
                            inputs={usesInputs}
                          />
                        ) : (
                          // Uses total only once a price or cost anchors them.
                          <InputsTrigger targets={capitalTargets(['purchase_price', 'total_cost'])} label="Total Uses">Not stated</InputsTrigger>
                        )}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          {stack.usesVariance || stack.sourcesVariance || stack.equityVsUses || (stack.suBalance && !stack.suBalance.balanced) || (plugged && stack.suBalance?.balanced) || (stack.suDerived && (stack.sources.length > 0 || stack.uses.length > 0)) ? (
            // The notes sit at the foot of the panel, under both totals.
            <div className="mt-auto shrink-0 space-y-1.5">
              {([['Uses', stack.usesVariance], ['Sources', stack.sourcesVariance]] as const).map(([side, variance]) =>
                variance ? (
                  <p key={side} data-testid={`su-variance-${side.toLowerCase()}`} className="rounded border border-amber-400/30 bg-amber-400/[0.06] px-2 py-1 text-[9.5px] text-amber-200">
                    The schedule states Total {side} of {formatAmount(variance.stated)}; its lines add to {formatAmount(variance.lines)}, {formatAmount(Math.abs(variance.difference))}{' '}
                    {variance.difference > 0 ? 'less' : 'more'}. The stated total is shown; the two are not added.
                  </p>
                ) : null,
              )}
              {stack.equityVsUses ? (
                <p data-testid="equity-vs-uses" className="rounded border border-amber-400/30 bg-amber-400/[0.06] px-2 py-1 text-[9.5px] text-amber-200">
                  The stated equity ({formatAmount(stack.equityVsUses.stated)}) is {formatAmount(Math.abs(stack.equityVsUses.difference))}{' '}
                  {stack.equityVsUses.difference > 0 ? 'more' : 'less'} than the stated uses less the debt call for ({formatAmount(stack.equityVsUses.byUses)}).
                  Both are kept as stated.
                </p>
              ) : stack.suBalance && !stack.suBalance.balanced ? (
                <p data-testid="su-imbalance" className="rounded border border-amber-400/30 bg-amber-400/[0.06] px-2 py-1 text-[9.5px] text-amber-200">
                  {stack.suBalance.text}.
                </p>
              ) : plugged && stack.suBalance?.balanced ? (
                <p data-testid="su-plug" className="text-[9.5px] text-stone-400">
                  Balanced by the implied equity: uses less debt. No document states the sponsor&rsquo;s equity.
                </p>
              ) : null}
              {stack.suDerived && (stack.sources.length > 0 || stack.uses.length > 0) ? (
                <p className="text-[9px] text-stone-500">
                  Derived: no sources &amp; uses schedule is stored on this deal. Sources are the capital stack; uses are
                  the recorded purchase price, costs and reserves{stack.usesComplete ? '' : ' — too few to total, so uses are not stated'}.
                </p>
              ) : null}
            </div>
          ) : null}
          </div>
        </DossierPanel>

        <DossierPanel title="Loan Terms & Pricing" dense className="min-w-0 xl:col-span-5">
          {/* One list in both modes, as the Economics Financing Summary: it
              used to switch to a 118px-label row of every editor when
              unlocked, which re-flowed the panel and dropped the held
              revision under the rate exactly when the rate was being typed.
              Unlocked, a stated term's value becomes its input in place, and
              an unstated one opens its input from its name in the line.
              The rows are grouped as a term sheet reads — structure and rate,
              term and amortization, fees and prepayment — and each group takes
              the height its rows need. */}
          <div data-testid="loan-terms" className="cs-panel-gap flex min-h-0 flex-1 flex-col gap-1.5">
            {termGroups.filter((group) => group.stated.length > 0).map((group) => (
              <section
                key={group.id}
                data-term-group={group.id}
                className="flex min-h-0 shrink-0 flex-col"
                style={{ flex: termsOutline ? '0 0 auto' : `${Math.ceil(group.stated.length / 2)} 0 auto` }}
              >
                <div className="cs-group-head mb-0.5 font-mono text-[8px] uppercase tracking-wider text-stone-600">{group.label}</div>
                <dl
                  className="grid flex-1 grid-flow-col gap-x-4 text-[10px]"
                  style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridTemplateRows: `repeat(${Math.ceil(group.stated.length / 2)}, auto)` }}
                >
                  {group.stated.map((row) => {
                    const shown = financingDisplay(row)
                    const editors = FINANCING_EDITORS[row.label] ?? []
                    const source = shown === '—' ? null : sourceOf(row.fieldName)
                    const marks = shown !== '—' ? <AmountMarks amountField={row.fieldName} sourceOf={sourceOf} /> : null
                    // A stated term keeps its state and Sponsor marks beside its
                    // input until it is typed over (see CapitalValue): a sponsor
                    // rate lost its tag exactly while it was being checked.
                    // A derived date (Maturity, Extension Maturity) is offered as an input: typing it states it.
                    const computed = source?.state === 'derived' && !isDerivedDateField(row.fieldName ?? '') ? source.formula ?? '' : undefined
                    const beside = marksBesideInput(edit, editors, marks, computed)
                    const locked = (
                      <>
                        {marks}
                        <span className="min-w-0 truncate" title={shown}>{shown}</span>
                      </>
                    )
                    return (
                      <div key={row.label} data-term={row.label} className="flex min-w-0 flex-col justify-center border-t border-white/[0.04]">
                        <div className="flex justify-between gap-2">
                          <dt className="shrink-0 text-stone-500">{row.label}</dt>
                          <dd className="flex min-w-0 items-center justify-end gap-1 text-right font-mono text-stone-200">
                            {editors.length > 0 ? (
                              <>
                                {beside ? marks : null}
                                <EditableValue
                                  name={editors[0].name}
                                  label={editors[0].label}
                                  display={shown}
                                  marks={beside ? null : marks}
                                  marksFirst
                                  fields={editors}
                                  computed={computed}
                                >
                                  {locked}
                                </EditableValue>
                              </>
                            ) : (
                              locked
                            )}
                          </dd>
                        </div>
                        {row.held ? (
                          <div data-held-revision className="text-right text-[9px] italic leading-tight text-amber-200/60" title="Decided in the record's Email Thread panel — see the pending-review strip above">
                            {row.held}
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </dl>
              </section>
            ))}
            {unstatedTerms.length > 0 ? (
              <div
                data-testid="terms-unstated"
                data-layout={termsOutline ? 'outline' : 'line'}
                className={
                  termsOutline
                    ? 'cs-outline flex min-h-0 flex-1 flex-col rounded border border-dashed border-white/[0.08] px-2.5 py-2 text-[9.5px] leading-snug text-stone-500'
                    : 'shrink-0 text-[9.5px] leading-snug text-stone-500'
                }
              >
                <span className="text-stone-400">Not stated on this deal:</span>{' '}
                {termsOutline ? (
                  // Laid out by group, one term to a line, in the room the few
                  // stated terms leave.
                  <div className="mt-1.5 grid flex-1 content-start gap-x-4 gap-y-2 sm:grid-cols-3">
                    {termGroups.filter((group) => group.unstated.length > 0).map((group) => (
                      <div key={group.id} data-unstated-group={group.id} className="min-w-0">
                        <div className="mb-1 font-mono text-[8px] uppercase tracking-wider text-stone-600">{group.label}</div>
                        <ul className="cs-unstated-list flex flex-col gap-1">
                          {group.unstated.map((row) => (
                            <li key={row.label}>
                              <InputsTrigger targets={FINANCING_EDITORS[row.label] ?? []} label={row.label}>
                                {row.label.toLowerCase()}
                              </InputsTrigger>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  unstatedTerms.map((row, index) => (
                    <Fragment key={row.label}>
                      {index > 0 ? ', ' : null}
                      <InputsTrigger targets={FINANCING_EDITORS[row.label] ?? []} label={row.label}>
                        {row.label.toLowerCase()}
                      </InputsTrigger>
                    </Fragment>
                  ))
                )}
                {termsOutline ? (
                  <div className="pt-1.5">{unlocked ? 'Select one to enter it.' : 'Unlock the dossier to enter them.'}</div>
                ) : (
                  <>.{' '}{unlocked ? 'Select one to enter it.' : 'Unlock the dossier to enter them.'}</>
                )}
              </div>
            ) : null}
            <div className="mt-auto shrink-0">
              <PanelLink label="Economics" onClick={() => onGoto?.('economics')} />
            </div>
          </div>
        </DossierPanel>

        <DossierPanel title="Covenants & Record" dense className="min-w-0 xl:col-span-3">
          <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 shrink-0 flex-col" style={{ flex: `${Math.max(2, stack.covenants.length)} 0 auto` }}>
          {stack.covenants.length > 0 ? (
            <table className="h-full w-full table-fixed text-left text-[10px]">
              <colgroup>
                <col />
                <col className="w-[27%]" />
                <col className="w-[26%]" />
                <col className="w-[9%]" />
              </colgroup>
              <thead className="text-[9px] uppercase tracking-wider text-stone-600">
                <tr className="h-px">
                  <th className="truncate px-1 py-1 !text-left font-medium">Covenant</th>
                  <th className="truncate px-1 py-1 !text-left font-medium">Requirement</th>
                  <th className="truncate px-1 py-1 !text-left font-medium">Current</th>
                  <th className="px-1 py-1 font-medium"><span className="sr-only">Compliance</span></th>
                </tr>
              </thead>
              <tbody>
                {stack.covenants.map((c) => {
                  // A term-sheet covenant is its catalogue field, edited in
                  // place; it printed as text with no editor anywhere on the
                  // tab, so correcting it meant Audit > All fields. Current
                  // restates the deal's own ratio, which the metric bar edits.
                  const catalogue = COVENANT_FIELDS[c.id]
                  // Covenant terms are the desk's: the sponsor's first reading
                  // stands, stamped, until the desk sets its own.
                  const ownerMark = catalogue ? <OwnershipMark source={sourceOf(catalogue.field)} /> : null
                  return (
                  <tr key={c.id} data-covenant={c.id} className="border-t border-white/[0.04] text-stone-300">
                    <td className="px-1 py-1">
                      <span className="cs-cov-name line-clamp-2" title={c.name}>{c.name}</span>
                      {ownerMark ? <span className="block">{ownerMark}</span> : null}
                    </td>
                    <td className="px-1 py-1 font-mono">
                      <CapitalValue
                        field={catalogue?.field}
                        label={c.name}
                        display={c.requirement}
                        reason={STRUCTURE_COVENANT_REASON}
                      />
                    </td>
                    <td className="px-1 py-1 font-mono">
                      <CapitalValue
                        label={`${c.name} current`}
                        display={c.current}
                        reason={
                          catalogue
                            ? `The deal’s own ${catalogue.current} today, not the covenant: edited on the metric bar above.`
                            : 'The deal’s own figure for this covenant today, not the covenant: edit that figure where it is shown.'
                        }
                      />
                      <HeadroomBar row={c} />
                    </td>
                    <td className="px-1 py-1">
                      {c.compliant == null ? (
                        <span className="text-stone-600">—</span>
                      ) : c.compliant ? (
                        <Check size={12} className="text-emerald-400" aria-label="Meets the requirement" />
                      ) : (
                        <X size={12} className="text-red-400" aria-label="Breaches the requirement" />
                      )}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <FillEmpty>
              <span className="text-[10px]">
                <InputsTrigger targets={COVENANT_TARGETS} label="Covenants">No covenants are stated on this deal.</InputsTrigger>
              </span>
            </FillEmpty>
          )}
          </div>
          {stack.proposedCovenants.length > 0 ? (
            // Held for a person, not the deal's covenants: no compliance mark.
            <div data-testid="proposed-covenants" className="mt-2 shrink-0 rounded border border-amber-400/20 bg-amber-400/[0.04] px-2 py-1.5 text-[10px]">
              <div className="mb-0.5 font-mono text-[8px] uppercase tracking-wider text-amber-300/80">Proposed — not yet on the deal</div>
              <ul className="space-y-0.5 text-stone-300">
                {stack.proposedCovenants.map((c) => (
                  <li key={c.id}>
                    {c.name} <span className="font-mono">{c.requirement}</span>{' '}
                    <span className="italic text-amber-200/60">— {c.held}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-0.5 text-[9px] text-stone-500">Decided in the record&rsquo;s Email Thread panel; see the pending-review strip above.</p>
            </div>
          ) : null}
          <div className="mt-2 flex min-h-0 shrink-0 flex-col border-t border-white/[0.05] pt-1.5" style={{ flex: `${stack.documents.length > 0 ? 0 : 1} 0 auto` }}>
            <div className="mb-1 flex items-center justify-between font-mono text-[8px] uppercase tracking-wider text-stone-500">
              <span>Documents ({documentsProp.length})</span>
              <PanelLink label="Documents" onClick={() => onGoto?.('documents')} />
            </div>
            {stack.documents.length === 0 ? (
              <FillEmpty compact><span className="text-[10px]">No documents are attached to this deal.</span></FillEmpty>
            ) : (
              <ul className="space-y-0.5">
                {stack.documents.slice(0, DOCUMENTS_SHOWN_TALL).map((doc, index) => (
                  <li key={doc.id} className={`${index >= DOCUMENTS_SHOWN ? 'cs-extra ' : ''}flex items-center gap-1.5 text-[10px] text-stone-300`}>
                    <span className="min-w-0 flex-1 truncate" title={doc.filename}>{doc.filename}</span>
                    {/* Nobody uploaded an intake original — it arrived — and a
                        bare dash where an uploader goes reads as lost data. */}
                    <span className="shrink-0 font-mono text-[9px] text-stone-600">{receivedByEmail(doc) ? 'by email' : when(doc.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="mt-1.5 flex min-h-0 shrink-0 flex-col border-t border-white/[0.05] pt-1.5" style={{ flex: `${stack.activity.length > 0 ? 0 : 1} 0 auto` }}>
            <div className="mb-1 flex items-center justify-between font-mono text-[8px] uppercase tracking-wider text-stone-500">
              <span>Latest activity</span>
              <PanelLink label="Audit" onClick={() => onGoto?.('audit')} />
            </div>
            {stack.activity.length === 0 ? (
              <FillEmpty compact><span className="text-[10px]">No notes or activity on this deal yet.</span></FillEmpty>
            ) : (
              <ul className="space-y-0.5">
                {stack.activity.slice(0, ACTIVITY_SHOWN_TALL).map((row, index) => (
                  <li key={row.id} className={`${index >= ACTIVITY_SHOWN ? 'cs-extra ' : ''}flex items-center justify-between gap-2 text-[10px] text-stone-300`}>
                    <span className="min-w-0 truncate">{row.title}</span>
                    <span className="shrink-0 font-mono text-[9px] text-stone-600">{when(row.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          </div>
        </DossierPanel>
      </div>
    </div>
  )
}

export default CapitalStackPage
